import {
  DEFAULT_UTC_OFFSET,
  formatDayMonthInOffset,
  formatHourMinuteInOffset,
  formatWeekdayShortInOffset
} from '@/utils/date'
import { DEADLINE_CALCULATION_SHORT_LABELS } from '@/constants/ds-attendance-sla.constants'
import type { ContainerStage } from '@/constants/ds-attendance-sla.constants'
import type { DSAttendanceDeadlineCalculationType } from '@/constants/ds-attendance.constants'
import type { DSAttendanceContainer } from '@/types/ds-attendance.types'

/** Faixa "Prazo de envio/auditoria · data · 48 h corridas" (mockups --lista, K1/K2). */

type SlaLineSource = Pick<
  DSAttendanceContainer,
  'sla' | 'stage' | 'slaWindowHours' | 'slaCalculationType' | 'timezone'
>

export interface ContainerSlaLine {
  phaseLabel: string | null
  deadline: string | null
  window: string | null
  /** Só quando não há prazo: por que o item está em "Sem SLA". */
  hint: string | null
}

/** `'qua 30/09 10:00'` no fuso do item (a escala), não no do navegador. */
export function formatContainerDeadline(iso: string, offset: string): string | null {
  const weekday = formatWeekdayShortInOffset(iso, offset)
  const day = formatDayMonthInOffset(iso, offset)
  const hour = formatHourMinuteInOffset(iso, offset)
  return weekday && day && hour ? `${weekday} ${day} ${hour}` : null
}

export function formatSlaWindow(
  hours: number | null | undefined,
  calculation: string | null | undefined
): string | null {
  if (hours === null || hours === undefined) return null
  const label = calculation
    ? DEADLINE_CALCULATION_SHORT_LABELS[calculation as DSAttendanceDeadlineCalculationType]
    : undefined
  return label ? `${hours} h ${label}` : `${hours} h`
}

const NO_SLA_HINTS: Record<ContainerStage, string> = {
  AWAITING_SUBMISSION: 'Sem prazo de envio',
  AWAITING_AUDIT: 'Política sem prazo de auditoria',
  IN_AUDIT: 'Política sem prazo de auditoria',
  CLOSED: 'Sem prazo de auditoria'
}

export function describeContainerSlaLine(container: SlaLineSource): ContainerSlaLine {
  const sla = container.sla ?? null
  if (!sla) {
    return {
      phaseLabel: null,
      deadline: null,
      window: null,
      hint: container.stage ? NO_SLA_HINTS[container.stage] : null
    }
  }
  const offset = container.timezone ?? DEFAULT_UTC_OFFSET
  return {
    phaseLabel: sla.phase === 'SEND' ? 'Prazo de envio' : 'Prazo de auditoria',
    deadline: sla.deadlineAt ? formatContainerDeadline(sla.deadlineAt, offset) : null,
    window: formatSlaWindow(container.slaWindowHours, container.slaCalculationType),
    hint: null
  }
}

export function containerSlaLineText(container: SlaLineSource): string {
  const line = describeContainerSlaLine(container)
  if (!line.phaseLabel) return line.hint ?? ''
  return [line.phaseLabel, line.deadline ?? '—', line.window].filter(Boolean).join(' · ')
}

/** Célula "Prazo" da Tabela: data em cima, fase embaixo (mockup --tabela). */
export function formatTableDeadline(
  container: SlaLineSource
): { date: string; phase: string } | null {
  const line = describeContainerSlaLine(container)
  if (!line.phaseLabel || !line.deadline) return null
  return { date: line.deadline, phase: container.sla?.phase === 'SEND' ? 'envio' : 'auditoria' }
}
